import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ChevronsUpDown, Wand2, X } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { normalizeError } from "@/shared/errors/normalizeError";
import { useGreenhouses, useGreenhouseBeds } from "@/features/farms/hooks/useGreenhouses";
import { useBaysByGreenhouseId } from "@/features/farms/hooks/useGreenhouseBays";
import { useRequestGreenhouseTransfer } from "../../hooks/useGreenhouseTransfers";
import type { StockEntryPipelineDto } from "../../services/sowingLifecycleApi";
import { allocateSourceBeds, isDestinationAllocationValid, isTransferQuantityValid, resolveSourcePlacement } from "./transferAllocations";

interface Props {
  entry: StockEntryPipelineDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RequestTransferDialog = ({ entry, open, onOpenChange, onSuccess }: Props) => {
  const notify = useNotification();
  const mutation = useRequestGreenhouseTransfer();

  const sourceAllocations = entry.greenhouseBedAllocations ?? [];
  const { placementKnown, greenhouseOnly, physicalTrays, wholeTrays } = resolveSourcePlacement(
    entry.greenhouseBedAllocations, entry.greenhouseBedIds, entry.physicalQuantity);
  const availableTrays = Math.max(0, Math.floor(entry.availableQuantity));
  const reserveTrays = Math.max(0, Math.floor(entry.fillingReserve));
  const transferableTrays = Math.min(wholeTrays, availableTrays + reserveTrays);
  const [transferTrays, setTransferTrays] = useState(transferableTrays);
  const quantityValid = isTransferQuantityValid(transferTrays, availableTrays, reserveTrays, physicalTrays, wholeTrays);

  // Destination selection
  const [destGreenhouseId, setDestGreenhouseId] = useState("");
  const [selectedBayIds, setSelectedBayIds] = useState<string[]>([]);
  const [destBedAllocations, setDestBedAllocations] = useState<Map<string, number>>(new Map());
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bayPopoverOpen, setBayPopoverOpen] = useState(false);
  const [bedPopoverOpen, setBedPopoverOpen] = useState(false);

  const greenhousesQuery = useGreenhouses();
  const destBaysQuery = useBaysByGreenhouseId(destGreenhouseId);
  const destBedsQuery = useGreenhouseBeds(destGreenhouseId);

  const bedsInSelectedBays = (destBedsQuery.data ?? []).filter(
    (b) => selectedBayIds.includes(b.bayId) && b.isActive
  );

  // Exclude the current greenhouse from the destination list
  const availableGreenhouses = (greenhousesQuery.data ?? []).filter(
    (g) => g.id !== entry.greenhouseId && g.isActive
  );

  const autoDistribute = () => {
    const beds = bedsInSelectedBays.filter((b) => destBedAllocations.has(b.id));
    if (beds.length === 0) return;
    let remaining = transferTrays;
    const result = new Map<string, number>();
    // Beds are uncapped: split evenly with no per-bed maximum.
    const share = Math.floor(transferTrays / beds.length);
    beds.forEach((bed, i) => {
      const allot = i === beds.length - 1 ? remaining : share;
      result.set(bed.id, allot);
      remaining -= allot;
    });
    setDestBedAllocations(result);
  };

  const allocatedTotal = Array.from(destBedAllocations.values()).reduce((a, b) => a + b, 0);
  const allocationDiff = allocatedTotal - transferTrays;
  // Uncapped beds: no per-bed maximum, so the capacity map is empty (unbounded).
  const allocationsValid = isDestinationAllocationValid(destBedAllocations,
    new Map(), transferTrays) &&
    (transferTrays !== physicalTrays ||
      destBedAllocations.size >= sourceAllocations.filter((bed) => (bed.wholeTrayCount ?? bed.trayCount) < bed.trayCount).length);

  const canSubmit =
    destGreenhouseId &&
    quantityValid &&
    placementKnown &&
    allocationsValid &&
    reason.trim().length > 0 &&
    !mutation.isPending;

  const handleDestGreenhouseChange = (gid: string) => {
    setDestGreenhouseId(gid);
    setSelectedBayIds([]);
    setDestBedAllocations(new Map());
  };

  const toggleBay = (bayId: string) => {
    setSelectedBayIds((prev) => {
      const next = prev.includes(bayId) ? prev.filter((x) => x !== bayId) : [...prev, bayId];
      setDestBedAllocations((current) => {
        const updated = new Map(current);
        for (const bedId of Array.from(updated.keys())) {
          const bed = destBedsQuery.data?.find((b) => b.id === bedId);
          if (!bed || !next.includes(bed.bayId)) updated.delete(bedId);
        }
        return updated;
      });
      return next;
    });
  };

  const toggleBed = (bedId: string) => {
    setDestBedAllocations((prev) => {
      const updated = new Map(prev);
      if (updated.has(bedId)) {
        updated.delete(bedId);
      } else {
        updated.set(bedId, 0);
      }
      return updated;
    });
  };

  const removeBed = (bedId: string) => {
    setDestBedAllocations((prev) => {
      const updated = new Map(prev);
      updated.delete(bedId);
      return updated;
    });
  };

  const updateBedCount = (bedId: string, value: number) => {
    setDestBedAllocations((prev) => {
      const updated = new Map(prev);
      updated.set(bedId, value);
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitError(null);

    const sourceBeds = allocateSourceBeds(sourceAllocations, transferTrays);
    if (!greenhouseOnly && sourceBeds.length === 0) return;

    const destinationBeds = Array.from(destBedAllocations.entries()).map(([bedId, count]) => ({
      greenhouseBedId: bedId,
      trayCount: count,
    }));

    mutation.mutate(
      {
        greenhouseStockEntryId: entry.id,
        destinationGreenhouseId: destGreenhouseId,
        transferTrayCount: transferTrays,
        sourceBeds,
        destinationBeds,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          notify.success("Transfer request submitted for GM approval");
          onSuccess();
        },
        onError: (err: unknown) => {
          const error = normalizeError(err);
          setSubmitError(error.fieldErrors.map((field) => field.userMessage).join(" ") || error.userMessage);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Greenhouse Transfer</DialogTitle>
          <DialogDescription>
            Transfer <strong>{entry.cropTypeName} — {entry.variety ?? "—"}</strong>{" "}
            from <strong>{entry.greenhouseName ?? "current greenhouse"}</strong>{" "}
            to another greenhouse. Requires GM approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <Field>
            <FieldLabel htmlFor="transfer-trays">Trays to transfer</FieldLabel>
            <Input id="transfer-trays" type="number" min={1} step={1} value={transferTrays || ""}
              onChange={(e) => setTransferTrays(Number(e.target.value))} />
            <p className="text-xs text-muted-foreground">
              {availableTrays} uncommitted sellable trays and {reserveTrays} filling-reserve trays can move.
              Partial moves use sellable trays first, then keep any moved reserve as reserve at the destination.
              For partial moves, customer reservations stay at the source.
              Moving all {physicalTrays} physical trays moves the entire entry, including its reservations.
            </p>
            {!placementKnown && <p className="text-xs text-destructive">
              {entry.greenhouseBedAllocations === undefined || !Number.isFinite(entry.physicalQuantity)
                ? "Source placement data is unavailable. Refresh after the API update before requesting a transfer."
                : "A source bed is recorded without tray counts. Reconcile its allocation before transferring."}
            </p>}
            {greenhouseOnly && <p className="text-xs text-muted-foreground">
              This stock has no source bed placement. The remainder will stay in {entry.greenhouseName ?? "the source greenhouse"} without a bed assignment.
            </p>}
            {placementKnown && !quantityValid && <p className="text-xs text-destructive">Enter 1–{transferableTrays} uncommitted whole trays.</p>}
            {transferTrays === physicalTrays && sourceAllocations.some((bed) => (bed.wholeTrayCount ?? bed.trayCount) < bed.trayCount) &&
              <p className="text-xs text-muted-foreground">Each partly used source tray needs a separate destination bed.</p>}
          </Field>
          {/* Destination Greenhouse */}
          <Field>
            <FieldLabel>Destination Greenhouse</FieldLabel>
            <Select value={destGreenhouseId} onValueChange={handleDestGreenhouseChange}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select greenhouse…" /></SelectTrigger>
              <SelectContent>
              {availableGreenhouses.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.greenhouseName}
                </SelectItem>
              ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Bay Selection */}
          {destGreenhouseId && (
            <Field>
              <FieldLabel>Destination Bay(s)</FieldLabel>
              <Popover open={bayPopoverOpen} onOpenChange={setBayPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {selectedBayIds.length > 0
                      ? `${selectedBayIds.length} bay(s) selected`
                      : "Select bays…"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] max-h-60 overflow-y-auto p-2">
                  {(destBaysQuery.data ?? [])
                    .filter((b) => b.isActive)
                    .map((bay) => (
                      <label
                        key={bay.id}
                        className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded"
                      >
                        <Checkbox
                          checked={selectedBayIds.includes(bay.id)}
                          onCheckedChange={() => toggleBay(bay.id)}
                        />
                        <span className="text-sm">{bay.bayName}</span>
                      </label>
                    ))}
                  {(destBaysQuery.data ?? []).length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">No bays found</p>
                  )}
                </PopoverContent>
              </Popover>
            </Field>
          )}

          {/* Bed Allocation */}
          {bedsInSelectedBays.length > 0 && (
            <Field>
              <FieldLabel>
                Destination Bed Allocations{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  (must total {transferTrays})
                </span>
              </FieldLabel>
              <Popover open={bedPopoverOpen} onOpenChange={setBedPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {destBedAllocations.size > 0
                      ? `${destBedAllocations.size} bed(s) selected`
                      : "Select beds…"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] max-h-60 overflow-y-auto p-2">
                  {bedsInSelectedBays.map((bed) => (
                    <label
                      key={bed.id}
                      className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent rounded"
                    >
                      <Checkbox
                        checked={destBedAllocations.has(bed.id)}
                        onCheckedChange={() => toggleBed(bed.id)}
                      />
                      <span className="text-sm flex-1">{bed.bedNumber}</span>
                      {bed.currentOccupancy > 0 && (
                        <span className="text-xs text-muted-foreground">{bed.currentOccupancy} trays on bed</span>
                      )}
                    </label>
                  ))}
                </PopoverContent>
              </Popover>

              {/* Tray count inputs per selected bed */}
              {destBedAllocations.size > 0 && (
                <div className="mt-2 space-y-2">
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={autoDistribute}
                    >
                      <Wand2 className="w-3 h-3" />
                      Auto-distribute
                    </Button>
                  </div>
                  {Array.from(destBedAllocations.entries()).map(([bedId, count]) => {
                    const bed = destBedsQuery.data?.find((b) => b.id === bedId);
                    return (
                      <div key={bedId} className="flex items-center gap-2">
                        <span className="text-sm min-w-[80px]">{bed?.bedNumber ?? bedId}</span>
                        <Input
                          type="number"
                          min={1}
                          value={count || ""}
                          onChange={(e) => updateBedCount(bedId, parseInt(e.target.value) || 0)}
                          className="h-8 w-24"
                          placeholder="Trays"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="ml-auto h-8 w-8 shrink-0"
                          aria-label={`Remove bed ${bed?.bedNumber ?? bedId}`}
                          onClick={() => removeBed(bedId)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                  {Array.from(destBedAllocations.values()).some((count) => count <= 0) && (
                    <p className="text-xs text-destructive">Give each selected bed at least 1 tray, or remove unused beds.</p>
                  )}
                  {allocationDiff !== 0 && (
                    <p className="text-xs text-destructive">
                      {allocationDiff > 0
                        ? `Over-allocated by ${allocationDiff} trays`
                        : `Under-allocated by ${Math.abs(allocationDiff)} trays`}
                    </p>
                  )}
                </div>
              )}
            </Field>
          )}

          {/* Reason */}
          <Field>
            <FieldLabel>Reason for transfer (required)</FieldLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this batch being moved?"
              rows={3}
            />
          </Field>

          {submitError && <p role="alert" className="text-sm text-destructive">{submitError}</p>}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {mutation.isPending ? "Submitting…" : "Request Transfer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
